import plugin from 'tailwindcss/plugin';
import { DESIGN } from './tokens';
import { toStr } from '@enonic/js-utils/value/toStr';

const BACKGROUND_COLORS = {
	neutral: DESIGN.color.background.default,
};

const BORDER_COLORS = {
	DEFAULT: DESIGN.color.border.neutral.subtle,
	neutral: {
		DEFAULT: DESIGN.color.border.neutral.default,
		inverted: DESIGN.color.border.neutral.inverted,
		subtle: DESIGN.color.border.neutral.subtle,
	},
	action: {
		primary: {
			DEFAULT: DESIGN.color.border.action.primary.default,
			hover: DESIGN.color.border.action.primary.hover,
			active: DESIGN.color.border.action.primary.active,
			disabled: DESIGN.color.border.action.primary.disabled,
		},
		disabled: DESIGN.color.border.action.disabled,
		secondary: {
			DEFAULT: DESIGN.color.border.action.secondary.default,
			hover: DESIGN.color.border.action.secondary.hover,
			active: DESIGN.color.border.action.secondary.active,
			disabled: DESIGN.color.border.action.secondary.disabled,
		},
		tertiary: {
			DEFAULT: DESIGN.color.border.action.tertiary.default,
			hover: DESIGN.color.border.action.tertiary.hover,
			active: DESIGN.color.border.action.tertiary.active,
			disabled: DESIGN.color.border.action.tertiary.disabled,
		},
		chip: {
			1: DESIGN.color.border.action.chip['01'].default,
			2: DESIGN.color.border.action.chip['02'].default,
			3: DESIGN.color.border.action.chip['03'].default,
			4: DESIGN.color.border.action.chip['04'].default,
			5: DESIGN.color.border.action.chip['05'].default,
			6: DESIGN.color.border.action.chip['06'].default,
			7: DESIGN.color.border.action.chip['07'].default,
			8: DESIGN.color.border.action.chip['08'].default,
			9: DESIGN.color.border.action.chip['09'].default,
			10: DESIGN.color.border.action.chip['10'].default,
			error: DESIGN.color.border.action.chip.error.default,
		},
		DEFAULT: DESIGN.color.border.action.default,
		hover: DESIGN.color.border.action.hover,
		active: DESIGN.color.border.action.active,
		focus: DESIGN.color.border.action.focus,
		subtle: DESIGN.color.border.action.subtle,
	},
	feedback: {
		success: DESIGN.color.border.feedback.success.default,
		warning: DESIGN.color.border.feedback.warning.default,
		info: DESIGN.color.border.feedback.info.default,
		error: DESIGN.color.border.feedback.error.default,
	},
};

const DIVIDER_COLORS = {
	DEFAULT: DESIGN.color.border.neutral.subtle,
	neutral: {
		DEFAULT: DESIGN.color.divider.neutral.default,
		inverted: DESIGN.color.divider.neutral.inverted,
	},
};

export const FONT_SIZE = {
	base: 'var(--font-size-base)',
	xs: DESIGN.font.xs,
	sm: DESIGN.font.sm,
	md: DESIGN.font.md,
	lg: DESIGN.font.lg,
	xl: DESIGN.font.xl,
	xxl: DESIGN.font.xxl,
	xxxl: DESIGN.font.xxxl,
};

const FONT_WEIGHT = {
	bold: DESIGN.font.weight.bold,
	semibold: DESIGN.font.weight.semi.bold,
	normal: DESIGN.font.weight.normal,
};

const ICON_COLORS = {
	feedback: {
		success: DESIGN.color.icon.feedback.success.default,
		warning: DESIGN.color.icon.feedback.warning.default,
		info: DESIGN.color.icon.feedback.info.default,
		error: DESIGN.color.icon.feedback.error.default,
	},
	action: {
		DEFAULT: DESIGN.color.icon.action.default,
		primary: {
			DEFAULT: DESIGN.color.icon.action.primary.default,
			hover: DESIGN.color.icon.action.primary.hover,
			active: DESIGN.color.icon.action.primary.active,
			disabled: DESIGN.color.icon.action.primary.disabled,
		},
		secondary: {
			DEFAULT: DESIGN.color.icon.action.secondary.default,
			hover: DESIGN.color.icon.action.secondary.hover,
			active: DESIGN.color.icon.action.secondary.active,
			disabled: DESIGN.color.icon.action.secondary.disabled,
		},
		tertiary: {
			DEFAULT: DESIGN.color.icon.action.tertiary.default,
			hover: DESIGN.color.icon.action.tertiary.hover,
			active: DESIGN.color.icon.action.tertiary.active,
			disabled: DESIGN.color.icon.action.tertiary.disabled,
		},
		inverted: DESIGN.color.icon.action.inverted,
		disabled: DESIGN.color.icon.action.disabled,
		subtle: DESIGN.color.icon.action.subtle,
	},
	neutral: {
		DEFAULT: DESIGN.color.icon.neutral.default,
	},
};

const PLACEHOLDER_COLORS = {
	DEFAULT: DESIGN.color.text.neutral.subtle,
};

const SPACING_TOKENS = {
	0: '0rem',
	xxs: DESIGN.spacing.xxs,
	xs: DESIGN.spacing.xs,
	sm: DESIGN.spacing.sm,
	md: DESIGN.spacing.md,
	lg: DESIGN.spacing.lg,
	xl: DESIGN.spacing.xl,
	xxl: DESIGN.spacing.xxl,
	xxxl: DESIGN.spacing.xxxl,
	auto: 'auto',
};

const SURFACE_COLORS = {
	action: {
		primary: {
			// WARNING: The DEFAULT property must be in UPPERCASE or class is not generated!
			DEFAULT: DESIGN.color.surface.action.primary.default,
			hover: DESIGN.color.surface.action.primary.hover,
			active: DESIGN.color.surface.action.primary.active,
			disabled: DESIGN.color.surface.action.primary.disabled,
		},
		secondary: {
			DEFAULT: DESIGN.color.surface.action.secondary.default,
			hover: DESIGN.color.surface.action.secondary.hover,
			active: DESIGN.color.surface.action.secondary.active,
			disabled: DESIGN.color.surface.action.secondary.disabled,
		},
		chip: {
			1: {
				DEFAULT: DESIGN.color.surface.action.chip['01'].default,
				hover: DESIGN.color.surface.action.chip['01'].hover,
			},
			2: {
				DEFAULT: DESIGN.color.surface.action.chip['02'].default,
				hover: DESIGN.color.surface.action.chip['02'].hover,
			},
			3: {
				DEFAULT: DESIGN.color.surface.action.chip['03'].default,
				hover: DESIGN.color.surface.action.chip['03'].hover,
			},
			4: {
				DEFAULT: DESIGN.color.surface.action.chip['04'].default,
				hover: DESIGN.color.surface.action.chip['04'].hover,
			},
			5: {
				DEFAULT: DESIGN.color.surface.action.chip['05'].default,
				hover: DESIGN.color.surface.action.chip['05'].hover,
			},
			6: {
				DEFAULT: DESIGN.color.surface.action.chip['06'].default,
				hover: DESIGN.color.surface.action.chip['06'].hover,
			},
			7: {
				DEFAULT: DESIGN.color.surface.action.chip['07'].default,
				hover: DESIGN.color.surface.action.chip['07'].hover,
			},
			8: {
				DEFAULT: DESIGN.color.surface.action.chip['08'].default,
				hover: DESIGN.color.surface.action.chip['08'].hover,
			},
			9: {
				DEFAULT: DESIGN.color.surface.action.chip['09'].default,
				hover: DESIGN.color.surface.action.chip['09'].hover,
			},
			10: {
				DEFAULT: DESIGN.color.surface.action.chip['10'].default,
				hover: DESIGN.color.surface.action.chip['10'].hover,
			},
			error: {
				DEFAULT: DESIGN.color.surface.action.chip.error.default,
				hover: DESIGN.color.surface.action.chip.error.hover,
			},
		},
		DEFAULT: DESIGN.color.surface.action.default,
		hover: DESIGN.color.surface.action.hover,
		disabled: DESIGN.color.surface.action.disabled,
		menu: {
			DEFAULT: DESIGN.color.surface.action.menu.default,
			active: DESIGN.color.surface.action.menu.active,
			hover: DESIGN.color.surface.action.menu.hover,
		},
	},
	feedback: {
		success: DESIGN.color.surface.feedback.success.default,
		warning: DESIGN.color.surface.feedback.warning.default,
		info: DESIGN.color.surface.feedback.info.default,
		error: DESIGN.color.surface.feedback.error.default,
	},
	neutral: {
		DEFAULT: DESIGN.color.surface.neutral.default,
		grey: DESIGN.color.surface.neutral.grey,
		primary: DESIGN.color.surface.neutral.primary,
		secondary: DESIGN.color.surface.neutral.secondary,
		subtle: DESIGN.color.surface.neutral.subtle,
		'subtle.02': DESIGN.color.surface.neutral.subtle02,
		'subtle.03': DESIGN.color.surface.neutral.subtle03,
	},
	statistic: {
		1: DESIGN.color.surface.statistic['1'],
		2: DESIGN.color.surface.statistic['2'],
		3: DESIGN.color.surface.statistic['3'],
		4: DESIGN.color.surface.statistic['4'],
		5: DESIGN.color.surface.statistic['5'],
		6: DESIGN.color.surface.statistic['6'],
		7: DESIGN.color.surface.statistic['7'],
		8: DESIGN.color.surface.statistic['8'],
		9: DESIGN.color.surface.statistic['9'],
		10: DESIGN.color.surface.statistic['10'],
	},
};

const TEXT_COLORS = {
	neutral: {
		DEFAULT: DESIGN.color.text.neutral.default,
		inverted: DESIGN.color.text.neutral.inverted,
		subtle: DESIGN.color.text.neutral.subtle,
		disabled: DESIGN.color.text.neutral.disabled,
	},
	feedback: {
		error: {
			DEFAULT: DESIGN.color.text.feedback.error.default,
		},
	},
	action: {
		DEFAULT: DESIGN.color.text.action.default,
		hover: DESIGN.color.text.action.hover,
		primary: {
			DEFAULT: DESIGN.color.text.action.primary.default,
			hover: DESIGN.color.text.action.primary.hover,
			active: DESIGN.color.text.action.primary.active,
			disabled: DESIGN.color.text.action.primary.disabled,
		},
		secondary: {
			DEFAULT: DESIGN.color.text.action.secondary.default,
			hover: DESIGN.color.text.action.secondary.hover,
			active: DESIGN.color.text.action.secondary.active,
			disabled: DESIGN.color.text.action.secondary.disabled,
		},
		tertiary: {
			DEFAULT: DESIGN.color.text.action.tertiary.default,
			hover: DESIGN.color.text.action.tertiary.hover,
			active: DESIGN.color.text.action.tertiary.active,
			disabled: DESIGN.color.text.action.tertiary.disabled,
		},
		chip: {
			1: DESIGN.color.text.action.chip['01'].default,
			2: DESIGN.color.text.action.chip['02'].default,
			3: DESIGN.color.text.action.chip['03'].default,
			4: DESIGN.color.text.action.chip['04'].default,
			5: DESIGN.color.text.action.chip['05'].default,
			6: DESIGN.color.text.action.chip['06'].default,
			7: DESIGN.color.text.action.chip['07'].default,
			8: DESIGN.color.text.action.chip['08'].default,
			9: DESIGN.color.text.action.chip['09'].default,
			10: DESIGN.color.text.action.chip['10'].default,
			error: DESIGN.color.text.action.chip.error.default,
		},
		active: DESIGN.color.text.action.active,
		disabled: DESIGN.color.text.action.disabled,
		inverted: DESIGN.color.text.action.inverted,
	},
};


export const HDIR_THEME_6_1_66 = {
	accentColor: SURFACE_COLORS,
	backgroundColor: {
		...BACKGROUND_COLORS,
		surface: SURFACE_COLORS,
	},
	borderColor: BORDER_COLORS,
	borderRadius: {
		none: '0px',
		xs: DESIGN.border.radius.xs,
		sm: DESIGN.border.radius.sm,
		md: DESIGN.border.radius.md,
		lg: DESIGN.border.radius.lg,
		full: '9999px',
	},
	borderWidth: {
		DEFAULT: DESIGN.border.sm,
		sm: DESIGN.border.sm,
		md: DESIGN.border.md,
		lg: DESIGN.border.lg,
	},
	boxShadowColor: SURFACE_COLORS,
	caretColor: TEXT_COLORS,
	extend: {
		boxShadow: {
			'design-md': DESIGN.shadow.md,
		},
		maxWidth: {
			'design-content': DESIGN.content.max.width,
		},
		width: {
			'design-content-mobile': DESIGN.content.width.mobile,
			'design-content-tablet': DESIGN.content.width.tablet,
		},
	},
	divideColor: DIVIDER_COLORS,
	fill: ICON_COLORS,
	fontSize: FONT_SIZE,
	fontWeight: FONT_WEIGHT,
	gap: {
		...SPACING_TOKENS,
		'design-component-mobile': `calc(${DESIGN.component.spacing.mobile} * 2)`,
		'design-component-tablet': `calc(${DESIGN.component.spacing.tablet} * 2)`,
		'design-component-desktop': `calc(${DESIGN.component.spacing.desktop} * 2)`,
	},
	gradientColorStops: SURFACE_COLORS,
	margin: {
		...SPACING_TOKENS,
		'design-component-mobile': DESIGN.component.spacing.mobile,
		'design-component-tablet': DESIGN.component.spacing.tablet,
		'design-component-desktop': DESIGN.component.spacing.desktop,
	},
	outlineColor: BORDER_COLORS,
	padding: {
		...SPACING_TOKENS,
		'design-component-mobile': DESIGN.component.spacing.mobile,
		'design-component-tablet': DESIGN.component.spacing.tablet,
		'design-component-desktop': DESIGN.component.spacing.desktop,
		'design-content-footer-mobile': DESIGN.content.footer.spacing.mobile,
		'design-content-footer-tablet': DESIGN.content.footer.spacing.tablet,
		'design-content-footer-desktop': DESIGN.content.footer.spacing.desktop,
	},
	placeholderColor: PLACEHOLDER_COLORS,
	ringColor: BORDER_COLORS,
	ringOffsetColor: BORDER_COLORS,
	scrollMargin: SPACING_TOKENS,
	scrollPadding: SPACING_TOKENS,
	space: SPACING_TOKENS,
	stroke: ICON_COLORS,
	textColor: {
		...TEXT_COLORS,
		icon: ICON_COLORS,
	},
	textDecorationColor: TEXT_COLORS,
} as const;


// export function extend({
// 	textColors = {},
// }: {
// 	textColors?: Record<string, string>
// }) {
// 	const extendedtextColors = {
// 		...TEXT_COLORS,
// 		...textColors,
// 	};
// 	return {
// 		accentColor: SURFACE_COLORS,
// 		backgroundColor: {
// 			...BACKGROUND_COLORS,
// 			surface: SURFACE_COLORS,
// 		},
// 		borderColor: BORDER_COLORS,
// 		borderRadius: {
// 			none: '0px',
// 			xs: DESIGN.border.radius.xs,
// 			sm: DESIGN.border.radius.sm,
// 			md: DESIGN.border.radius.md,
// 			lg: DESIGN.border.radius.lg,
// 			full: '9999px',
// 		},
// 		borderWidth: {
// 			DEFAULT: DESIGN.border.sm,
// 			sm: DESIGN.border.sm,
// 			md: DESIGN.border.md,
// 			lg: DESIGN.border.lg,
// 		},
// 		boxShadow: {
// 			'design-md': DESIGN.shadow.md,
// 		},
// 		boxShadowColor: SURFACE_COLORS,
// 		caretColor: extendedtextColors,
// 		divideColor: DIVIDER_COLORS,
// 		fill: ICON_COLORS,
// 		fontSize: FONT_SIZE,
// 		fontWeight: FONT_WEIGHT,
// 		gap: {
// 			...SPACING_TOKENS,
// 			'design-component-mobile': `calc(${DESIGN.component.spacing.mobile} * 2)`,
// 			'design-component-tablet': `calc(${DESIGN.component.spacing.tablet} * 2)`,
// 			'design-component-desktop': `calc(${DESIGN.component.spacing.desktop} * 2)`,
// 		},
// 		gradientColorStops: SURFACE_COLORS,
// 		margin: {
// 			...SPACING_TOKENS,
// 			'design-component-mobile': DESIGN.component.spacing.mobile,
// 			'design-component-tablet': DESIGN.component.spacing.tablet,
// 			'design-component-desktop': DESIGN.component.spacing.desktop,
// 		},
// 		maxWidth: {
// 			'design-content': DESIGN.content.max.width,
// 		},
// 		outlineColor: BORDER_COLORS,
// 		padding: {
// 			...SPACING_TOKENS,
// 			'design-component-mobile': DESIGN.component.spacing.mobile,
// 			'design-component-tablet': DESIGN.component.spacing.tablet,
// 			'design-component-desktop': DESIGN.component.spacing.desktop,
// 			'design-content-footer-mobile': DESIGN.content.footer.spacing.mobile,
// 			'design-content-footer-tablet': DESIGN.content.footer.spacing.tablet,
// 			'design-content-footer-desktop': DESIGN.content.footer.spacing.desktop,
// 		},
// 		placeholderColor: PLACEHOLDER_COLORS,
// 		ringColor: BORDER_COLORS,
// 		ringOffsetColor: BORDER_COLORS,
// 		scrollMargin: SPACING_TOKENS,
// 		scrollPadding: SPACING_TOKENS,
// 		space: SPACING_TOKENS,
// 		stroke: ICON_COLORS,
// 		textColor: {
// 			...extendedtextColors,
// 			icon: ICON_COLORS,
// 		},
// 		textDecorationColor: extendedtextColors,
// 		width: {
// 			'design-content-mobile': DESIGN.content.width.mobile,
// 			'design-content-tablet': DESIGN.content.width.tablet,
// 		},
// 	} as const;
// }

export const headingFn = ({
	addBase,
}: {
	addBase: (params: Record<string, Record<string, string>>) => void
}): void => {
	addBase({
		h1: {
			fontSize: DESIGN.font.xxxl,
			fontWeight: DESIGN.font.weight.semi.bold,
		},
		h2: {
			fontSize: DESIGN.font.xxl,
			fontWeight: DESIGN.font.weight.semi.bold,
		},
		h3: {
			fontSize: DESIGN.font.xl,
			fontWeight: DESIGN.font.weight.semi.bold,
		},
		h4: {
			fontSize: DESIGN.font.lg,
			fontWeight: DESIGN.font.weight.semi.bold,
		},
		h5: {
			fontSize: DESIGN.font.md,
			fontWeight: DESIGN.font.weight.semi.bold,
		},
	});
};

export const hdirTailwindPlugin_6_1_66 = plugin(
	headingFn,
	{
		theme: HDIR_THEME_6_1_66,
	},
);
